import { useCallback } from 'react'

import { useLocation } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Loading } from '@components/Loading'
import { Separator } from '@components/Separator'
import { DownstreamSwipe } from '@containers/Task/Actions/DownstreamSwipe'
import { LoaderContainer, NoTasksText } from '@containers/Task/Lists/styles'
import { DownstreamTile } from '@containers/Task/Tiles/DownstreamTile'
import { useSearch } from '@contexts/search'
import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { useFindTasks } from '@hooks/tasks/find'

type TViewType = 'mechanical' | 'process'

export function SearchDownstreamTasks() {
  const { searchTerm } = useSearch()
  const theme = useTheme()

  const { getWeekRange } = useDate()
  const { selectedDate } = useSharedDates()
  const { weekStart, weekEnd } = getWeekRange(selectedDate)
  const location = useLocation()
  const name = location.pathname.split('/').filter(Boolean).pop() || ''

  const { data: rawTasks, isLoading } = useFindTasks({
    startDate: weekStart,
    endDate: weekEnd,
    view: name as TViewType,
  })

  const tasks = rawTasks ?? []
  type TTask = (typeof tasks)[number]

  const searchResults = useCallback(
    (task: TTask) => {
      if (!searchTerm || searchTerm.length < 2) return false

      const fieldsToSearch = [
        task.name,
        task.jobName,
        task.equipmentTag,
        task.operationLongText,
        task.workOrder,
        typeof task.location === 'string' ? task.location : null,
        task.functionalLocation,
      ]

      return fieldsToSearch.some((field) =>
        field?.toLowerCase().includes(searchTerm),
      )
    },
    [searchTerm],
  )

  const tasksResult = tasks
    .filter(searchResults)
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

  const renderItem = useCallback(
    (task: TTask) => (
      <DownstreamSwipe task={task}>
        <DownstreamTile task={task} />
      </DownstreamSwipe>
    ),
    [],
  )

  return (
    <>
      {isLoading ? (
        <LoaderContainer>
          <Loading color={theme.colors.text} size="small" />
        </LoaderContainer>
      ) : tasksResult.length > 0 && searchTerm ? (
        <div>
          {tasksResult.map((task, index) => (
            <div key={task.id.toString()}>
              {index > 0 && <Separator />}
              {renderItem(task)}
            </div>
          ))}
        </div>
      ) : (
        <LoaderContainer>
          <NoTasksText>Oops, no tasks found.</NoTasksText>
        </LoaderContainer>
      )}
    </>
  )
}
